import React, { useState, useMemo } from "react";
import axios from "axios";
import { TeamEmailEntry } from "./entities";
import {
  Permission,
  InputShareFieldProps,
  EntityType,
  ResourceType,
} from "./entities";
import { createLogger } from "debug/DebugingLogger";
import { UserModelRole } from "target_wasm";
import { EmailList } from "./EmailList";
import {
  EmailValidation,
  parseEmails,
  validateEmails,
} from "./EmailValidation";
import { EmailInput } from "./EmailInput";
import {
  SelectDropdown,
  SelectOption,
} from "components/main/utils/SelectDropdown";
import { Button } from "components/common/Button";
import { EmailChipInput, EmailChip } from "./EmailChipInput";
import { useShareScope } from "./useShareScope";

const log = createLogger("ModelServices");

const CAPACITY_FALLBACK =
  "We've hit our account limit, so we couldn't invite everyone yet.";
const SHARE_FALLBACK = "Failed to share. Please try again.";

/// Refusals from the share doors carry their own wording, because only the
/// server knows how much it may say: a company org's table names the addresses
/// it turned away, a personal one must not — naming them there would answer
/// "does this address have a MyCorr account" for anyone who asked.
const shareErrorMessage = (e: unknown): string => {
  if (!axios.isAxiosError(e)) return SHARE_FALLBACK;
  const data = e.response?.data;
  if (data?.status === "capacity_reached")
    return data.message ?? CAPACITY_FALLBACK;
  if (typeof data?.reason !== "string") return SHARE_FALLBACK;
  const refused: string[] = Array.isArray(data.refused_emails)
    ? data.refused_emails
    : [];
  return refused.length ? `${data.reason} ${refused.join(", ")}` : data.reason;
};

interface ShareModeProps extends InputShareFieldProps {
  entityType: EntityType.User;
}

interface TeamModeProps {
  entityType: EntityType.Team;
  teamMembers: TeamEmailEntry[];
  setTeamMembers: (members: TeamEmailEntry[]) => void;
  isSubmitting: boolean;
  currentUserEmail?: string;
  isCreatingTeam?: boolean;
  onCancel?: () => void;
  onAdd?: () => void;
  canRevokeAccess?: boolean;
}

type EmailSectionProps = ShareModeProps | TeamModeProps;

export const UserEmailSection: React.FC<ShareModeProps> = (props) => {
  const [chips, setChips] = useState<EmailChip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  // Company-org tables restrict entry to the org roster (D19) — the picker
  // makes the door's membership refusal nearly unreachable.
  const shareScope = useShareScope(props.entityId, props.resourceType);

  const defaultPermission =
    props.resourceType === ResourceType.Model
      ? (props.defaultRole ?? UserModelRole.Collaborator)
      : (props.defaultPermission ?? Permission.CanImport);

  const [selectedPermission, setSelectedPermission] = useState<
    UserModelRole | Permission
  >(defaultPermission);

  const permissionOptions: SelectOption[] = useMemo(() => {
    if (props.resourceType === ResourceType.Model) {
      return [UserModelRole.Collaborator, UserModelRole.Viewer].map((role) => ({
        label: UserModelRole[role],
        value: role,
      }));
    }
    return [
      { label: "Can Import", value: Permission.CanImport },
      { label: "Can Share", value: Permission.CanShare },
    ];
  }, [props.resourceType]);

  const handleShare = async () => {
    const validEmails = chips
      .filter((chip) => chip.status === "valid")
      .map((chip) => chip.email);

    if (chips.length === 0) {
      setError("Add at least one email to share.");
      return;
    }

    if (validEmails.length === 0) {
      setError("Resolve the highlighted emails before sharing.");
      return;
    }

    try {
      setIsSharing(true);
      const outcome = await props.shareAction(validEmails, selectedPermission);
      setChips([]);
      const atCapacity = outcome?.atCapacityEmails ?? [];
      if (atCapacity.length > 0) {
        setError(`${CAPACITY_FALLBACK} Not invited: ${atCapacity.join(", ")}`);
        setSuccessMessage(null);
        return;
      }
      // A door that serves what it can (datasets) names what it refused —
      // without this the dialog said "shared successfully" over a partial
      // failure.
      const refused = outcome?.refusedEmails ?? [];
      if (refused.length > 0) {
        setError(`Shared, but not with: ${refused.join(", ")}`);
        setSuccessMessage(null);
        return;
      }
      setError(null);
      const resourceLabel =
        props.resourceType === ResourceType.Model
          ? "Model"
          : props.resourceType === ResourceType.Dataset
            ? "Dataset"
            : "Table";
      setSuccessMessage(`${resourceLabel} shared successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (shareError) {
      log.error("Failed to share:", shareError);
      setError(shareErrorMessage(shareError));
      setSuccessMessage(null);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 overflow-visible">
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-2">
          <EmailChipInput
            chips={chips}
            onChipsChange={setChips}
            entityId={props.entityId}
            resourceType={props.resourceType}
            error={error}
            onErrorChange={setError}
            directory={shareScope.directory}
            directoryLabel={shareScope.orgName}
            placeholder={
              shareScope.scope === "company"
                ? `Members of ${shareScope.orgName ?? "the organization"}`
                : undefined
            }
          />

          <div className="flex items-center gap-2 justify-end">
            {error ? (
              <span
                className="text-error text-sm mr-auto truncate"
                role="alert"
              >
                {error}
              </span>
            ) : successMessage ? (
              <span className="text-success mr-auto" role="status">
                {successMessage}
              </span>
            ) : null}
            {/* Datasets are flat (no role tier) — the composer offers no role
                picker; the share endpoint ignores any role. */}
            {props.resourceType !== ResourceType.Dataset && (
              <SelectDropdown
                options={permissionOptions}
                value={selectedPermission}
                onChange={(value) =>
                  setSelectedPermission(value as UserModelRole | Permission)
                }
                className="w-30! [&_span]:whitespace-nowrap!"
                menuWidthClass="w-[118px]"
                size="md"
                centerOptions={true}
              />
            )}
            <Button
              onClick={handleShare}
              disabled={
                isSharing ||
                props.shareDisabled ||
                chips.every((chip) => chip.status !== "valid")
              }
              loading={isSharing}
              variant="primary"
              size="sm"
              className="h-8! px-6!"
            >
              Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TeamEmailSection: React.FC<TeamModeProps> = (props) => {
  const [inputValue, setInputValue] = useState("");
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [duplicateEmails, setDuplicateEmails] = useState<string[]>([]);

  const getExistingEmails = () => {
    const teamEmails = props.teamMembers.map((member) =>
      member.email.toLowerCase(),
    );
    const currentUserEmail =
      props.isCreatingTeam && props.currentUserEmail
        ? [props.currentUserEmail.toLowerCase()]
        : [];
    return [...teamEmails, ...currentUserEmail];
  };

  const handleAddEmails = () => {
    const parsedEmails = parseEmails(inputValue);
    const validEmails = validateEmails(parsedEmails);
    const existingEmails = getExistingEmails();
    const invalids = parsedEmails.filter(
      (email) => !validEmails.includes(email),
    );

    const duplicates: string[] = [];
    const uniqueValid = validEmails.filter((email) => {
      const emailLower = email.toLowerCase();
      const isDuplicate = existingEmails.includes(emailLower);

      if (isDuplicate) {
        duplicates.push(email);
      }

      return !isDuplicate;
    });

    setInvalidEmails(invalids);
    setDuplicateEmails(duplicates);

    if (uniqueValid.length > 0) {
      const newMembers = uniqueValid.map((email) => ({
        email: email.toLowerCase(),
        accessLevel: "Member",
      }));
      props.setTeamMembers([...props.teamMembers, ...newMembers]);
      setInputValue("");
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddEmails();
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    props.setTeamMembers(
      props.teamMembers.filter((member) => member.email !== emailToRemove),
    );
  };

  const handleAccessLevelChange = (email: string, newAccessLevel: string) => {
    props.setTeamMembers(
      props.teamMembers.map((member) =>
        member.email === email
          ? { ...member, accessLevel: newAccessLevel }
          : member,
      ),
    );
  };

  const displayItems =
    props.isCreatingTeam && props.currentUserEmail
      ? [
          { email: props.currentUserEmail, accessLevel: "Admin" },
          ...props.teamMembers,
        ]
      : props.teamMembers;

  return (
    <div className="overflow-visible mt-2">
      <EmailInput
        value={inputValue}
        onChange={(value) => {
          setInputValue(value);
          if (invalidEmails.length) setInvalidEmails([]);
          if (duplicateEmails.length) setDuplicateEmails([]);
        }}
        onKeyDown={handleKeyPress}
      />
      <EmailValidation
        invalidEmails={invalidEmails}
        duplicateEmails={duplicateEmails}
      />

      <EmailList
        items={displayItems}
        isShareMode={false}
        isTeamMode
        currentUserEmail={props.currentUserEmail}
        isCreatingTeam={props.isCreatingTeam}
        onAccessLevelChange={handleAccessLevelChange}
        onRemove={handleRemoveEmail}
        onTeamAdd={props.onAdd}
        onTeamCancel={props.onCancel}
        canRevokeAccess={props.canRevokeAccess ?? false}
      />
    </div>
  );
};

export const EmailSection: React.FC<EmailSectionProps> = (props) => {
  if (props.entityType === EntityType.User) {
    return <UserEmailSection {...(props as ShareModeProps)} />;
  }

  return <TeamEmailSection {...(props as TeamModeProps)} />;
};
